export const POPULATION_SOURCE_URL = 'https://www.caa.gov.cz/provoz/bezpilotni-letadla/specificka-kategorie-specific/sora-2-5/posuzovani-hustoty-zalidneni-v-kalkulacce-sora-2-5/';
export const EASA_POPULATION_MAP_URL = 'https://experience.arcgis.com/experience/b00a6ce43d1943959d21bc957de265f4/';

// Selected LUISA classes from the CAA guidance; 1221 is deliberately not a density.
export const LUISA_DENSITIES = Object.freeze({
  1111: 13340, 1121: 8402, 1130: 3937, 1210: 6436, 1222: 49999,
  1230: 7297, 1242: 49999, 1330: 3443, 1410: 9912, 1421: 4475,
  1422: 8128, 3310: 49999,
});
export const LUISA_CODES = [...Object.keys(LUISA_DENSITIES), '1221'].sort();

// EASA Experience Builder combines multiple widgets' hash params with '&' (this app only ever sets two:
// widget_1's Map center and widget_120's Search text) — a single widget's own params are ','-joined
// instead (e.g. widget_1's "center:...,scale:5000" above). GPS is authoritative for centering; address is
// only ever an optional convenience search hint layered on top, never a substitute for it.
export function getPopulationMapUrl(latitude, longitude, address) {
  const hasCoordinates = typeof latitude === 'number' && typeof longitude === 'number'
    && Number.isFinite(latitude) && Number.isFinite(longitude)
    && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
  const base = `${EASA_POPULATION_MAP_URL}?locale=cs`;

  const widgets = [];
  if (hasCoordinates) widgets.push(`widget_1=center:${encodeURIComponent(`${longitude},${latitude},4326`)},scale:5000`);
  const text = typeof address === 'string' ? address.trim() : '';
  if (text) widgets.push(`widget_120=text:${encodeURIComponent(text)}`);

  return widgets.length ? `${base}#${widgets.join('&')}` : base;
}

/** Whether a getPopulationMapUrl() result actually centers on the mission's GPS — distinct from the URL
 * merely containing '#', since an address-only hint (no valid GPS) also produces a '#' fragment now. */
export function populationMapUrlHasCoordinates(url) {
  return url.includes('#widget_1=center:') || url.includes('&widget_1=center:');
}

export function assessPopulationRow(row) {
  const errors = [];
  if (!row.name.trim()) errors.push('name');
  const raw = row.census.trim();
  // Keep text input intact so malformed values remain visible, including NaN/Infinity.
  const census = raw === '' ? null : Number(raw.replace(',', '.'));
  if (raw !== '' && (!/^\+?(?:\d+(?:[.,]\d*)?|[.,]\d+)$/.test(raw)
    || !Number.isFinite(census) || census < 0)) errors.push('census');
  const assigned = LUISA_DENSITIES[row.luisa];
  if (row.luisa !== 'unavailable' && row.luisa !== '1221' && assigned === undefined) errors.push('luisa');
  if (errors.length) return { density: null, source: null, errors, reason: 'invalid' };
  if (census !== null && (assigned === undefined || row.luisa === '1111' || row.luisa === '1121')) {
    return { density: census, source: 'CENSUS', errors, reason: 'census' };
  }
  if (assigned !== undefined) return { density: assigned, source: 'LUISA', errors, reason: 'luisa' };
  return { density: null, source: null, errors, reason: row.luisa === '1221' ? 'unusable' : 'missing' };
}

export function assessPopulationRows(rows) {
  const results = rows.map(assessPopulationRow);
  const valid = results.filter(result => result.density !== null);
  return {
    results, validCount: valid.length,
    complete: results.length > 0 && valid.length === results.length,
    maximum: valid.length ? Math.max(...valid.map(result => result.density)) : null,
  };
}

export const createPopulationRow = () => ({ id: crypto.randomUUID(), name: '', census: '', luisa: 'unavailable', example: false });

/** Maps a PUT/GET /missions/{id}/population_assessment response row back to the editable row shape
 * (server-derived density/source/reason/errors are re-derived client-side via assessPopulationRow on
 * every render — never displayed as separately-tracked server values — so there is exactly one place
 * that decides what a row's result looks like). */
export const rowFromServer = serverRow => ({
  id: serverRow.id, name: serverRow.name, census: serverRow.census ?? '', luisa: serverRow.luisa, example: false,
});

/** Payload for PUT /missions/{id}/population_assessment — raw operator input only (id/name/census/luisa);
 * density/source/reason are always recomputed server-side, never sent. Example rows are never submitted. */
export const toSaveRequest = rows => ({
  rows: rows.filter(row => !row.example).map(({ id, name, census, luisa }) => ({ id, name, census, luisa })),
});

// Tab/route remounts retain in-progress (unsaved) edits in this browser runtime only, scoped per
// user/organization/mission/geometry — see hydrated/dirty/savedAt below for how a save moves the source
// of truth to the server without losing that same-session scratch-pad behavior.
export function createPopulationDraftStore() {
  const drafts = new Map();
  let activeScope;
  return {
    get(user, mission) {
      const scope = JSON.stringify([user?.id, user?.organization_id, mission.organization_id]);
      if (scope !== activeScope) { drafts.clear(); activeScope = scope; }
      // The take-off point and the one flight area (+ its corridor width or circle radius) — mission-map
      // unification PR6; flight_plan_polygon is no longer a geometry source.
      const geometry = JSON.stringify([
        mission.latitude, mission.longitude, mission.flight_area, mission.corridor_width_m, mission.circle_radius_m,
      ]);
      let draft = drafts.get(mission.id);
      if (!draft || draft.geometry !== geometry) {
        draft = {
          geometry, rows: [createPopulationRow()], key: crypto.randomUUID(),
          // hydrated: whether a GET has been attempted yet (successful or not) — attempted only once per
          // draft so a later remount never clobbers in-progress typing with a stale server response.
          hydrated: false,
          // dirty: whether rows differ from the last save/load — never auto-saved, only used for the UI's
          // "unsaved changes" indicator and to skip hydration if the user started typing before GET resolved.
          dirty: false,
          savedAt: null,
          // Cached summary of the last known *saved* assessment (set alongside hydrated/savedAt) — read
          // back on every mount, including one reusing an already-hydrated draft from an earlier visit
          // in this session, so a caller reporting this outward (e.g. a SAIL banner) never has to
          // re-fetch just to redisplay what's already known.
          maximum: null,
          complete: false,
        };
        drafts.set(mission.id, draft);
      }
      return draft;
    },
  };
}
